import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../config';

interface User {
  id: number;
  name: string;
  username: string;
  email: string;
  phone_number: string;
  address: string;
  group_id: number;
  company_id: number;
  user_type: string;
  lang_id: number;
  group?: {
    id: number;
    title: string;
  };
}

interface Permission {
  module_id: number;
  module_name: string;
  module_route: string;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_print: boolean;
  can_export: boolean;
}

interface AccessibleModule {
  id: number;
  title: string;
  route: string;
  position: number;
  level: string;
  parent_id: number | null;
  is_report: boolean;
  permissions: {
    can_add: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_print: boolean;
    can_export: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  permissions: Permission[];
  accessibleModules: AccessibleModule[];
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  getCurrentUser: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  hasPermission: (moduleRoute: string, action: 'add' | 'edit' | 'delete' | 'print' | 'export') => boolean;
  canAccessModule: (moduleRoute: string) => boolean;
  getUserPermissions: () => Permission[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [accessibleModules, setAccessibleModules] = useState<AccessibleModule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = async (username: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.post<{
        user: User;
        token: string;
        permissions: Permission[];
        accessible_modules: AccessibleModule[];
      }>(API_ENDPOINTS.LOGIN, { username, password });

      if (response.success) {
        setUser(response.data.user);
        setToken(response.data.token);
        setPermissions(response.data.permissions || []);
        setAccessibleModules(response.data.accessible_modules || []);
        localStorage.setItem('auth_token', response.data.token);
        return true;
      } else {
        setError(response.message || 'Login failed');
        return false;
      }
    } catch (err) {
      setError('Network error. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setPermissions([]);
    setAccessibleModules([]);
    localStorage.removeItem('auth_token');
  };

  // Check if user has permission for a specific action on a module
  const hasPermission = (moduleRoute: string, action: 'add' | 'edit' | 'delete' | 'print' | 'export'): boolean => {
    const permission = permissions.find(p => p.module_route === moduleRoute);
    if (!permission) return false;

    switch (action) {
      case 'add':
        return permission.can_add;
      case 'edit':
        return permission.can_edit;
      case 'delete':
        return permission.can_delete;
      case 'print':
        return permission.can_print;
      case 'export':
        return permission.can_export;
      default:
        return false;
    }
  };

  // Check if user can access a specific module
  const canAccessModule = (moduleRoute: string): boolean => {
    const canAccess = accessibleModules.some(m => m.route === moduleRoute);
    return canAccess;
  };

  // Get all user permissions
  const getUserPermissions = (): Permission[] => {
    return permissions;
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    if (token) {
      getCurrentUser();
    }
  }, [token]);

  const getCurrentUser = async () => {
    try {
      const response = await apiService.get<{
        user: User;
        permissions: Permission[];
        accessible_modules: AccessibleModule[];
      }>(API_ENDPOINTS.ME);

      if (response.success) {
        setUser(response.data.user);
        setPermissions(response.data.permissions || []);
        setAccessibleModules(response.data.accessible_modules || []);
      } else {
        // Token might be invalid, logout
        logout();
      }
    } catch (error) {
      console.error('Error getting current user:', error);
      // Token might be invalid, logout
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    token,
    permissions,
    accessibleModules,
    login,
    logout,
    getCurrentUser,
    isLoading,
    error,
    hasPermission,
    canAccessModule,
    getUserPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 